import React, { useState } from 'react';
import { useCategoryTourDetails } from './useCategoryTourDetails';
import { Card, CardContent, CardHeader, CardTitle } from 'dashboard/components/ui/Card';
import { Button } from 'dashboard/components/ui/Button';
import { Input } from 'dashboard/components/ui/Input';
import { Label } from 'dashboard/components/ui/Label';
import { Textarea } from 'dashboard/components/ui/Textarea';
import { Badge } from 'dashboard/components/ui/Badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from 'dashboard/components/ui/Dialog';
import { Trash2 } from 'lucide-react';
import './_CategoryTourDetails.scss';

const CategoryTourDetails: React.FC = () => {
  const {
    selectedCategoryTour,
    formData,
    handleInputChange,
    handleSubmit,
    handleDelete,
    t,
  } = useCategoryTourDetails();

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  if (!selectedCategoryTour) return <></>;

  const handleDeleteConfirm = () => {
    handleDelete();
    setIsDeleteDialogOpen(false);
  };

  return (
    <section className="content mt-3">
      <Card>
        <CardHeader>
          <CardTitle>
            {t('dashboard.categoryTourPage.editTitle') || 'Modifier la catégorie de tour'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              {/* Nom FR */}
              <div>
                <Label htmlFor="name">
                  {t('dashboard.categoryTourPage.name') || 'Nom'} * (FR)
                </Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
              </div>

              {/* Nom EN */}
              <div>
                <Label htmlFor="name_en">
                  {t('dashboard.categoryTourPage.name') || 'Nom'} (EN)
                </Label>
                <Input
                  id="name_en"
                  name="name_en"
                  value={formData.name_en || ''}
                  onChange={handleInputChange}
                  placeholder="English name"
                />
              </div>

              {/* Description FR */}
              <div>
                <Label htmlFor="description">
                  {t('dashboard.categoryTourPage.description') || 'Description'} * (FR)
                </Label>
                <Textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={5}
                  required
                />
              </div>

              {/* Description EN */}
              <div>
                <Label htmlFor="description_en">
                  {t('dashboard.categoryTourPage.description') || 'Description'} (EN)
                </Label>
                <Textarea
                  id="description_en"
                  name="description_en"
                  value={formData.description_en || ''}
                  onChange={handleInputChange}
                  rows={5}
                  placeholder="English description"
                />
              </div>

              {/* Informations */}
              <div className="category-tour-info-modern">
                <div className="info-item">
                  <span className="info-label">{t('dashboard.categoryTourPage.slug') || 'Slug'}:</span>
                  <Badge variant="default">{selectedCategoryTour.slug}</Badge>
                </div>
                <div className="info-item">
                  <span className="info-label">{t('dashboard.categoryTourPage.addedBy') || 'Créé par'}:</span>
                  <span className="info-value">{selectedCategoryTour.addedBy.username}</span>
                </div>
                {selectedCategoryTour.createdAt && (
                  <div className="info-item">
                    <span className="info-label">
                      {t('dashboard.categoryTourPage.createdAt') || 'Date de création'}:
                    </span>
                    <span className="info-value">
                      {new Date(selectedCategoryTour.createdAt).toLocaleDateString('fr-FR')}
                    </span>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', paddingTop: '1rem' }}>
                <Button
                  type="button"
                  variant="destructive"
                  onClick={() => setIsDeleteDialogOpen(true)}
                >
                  <Trash2 size={16} />
                  {t('dashboard.categoryTourPage.deleteButton') || 'Supprimer'}
                </Button>
                <Button type="submit" variant="default">
                  {t('dashboard.categoryTourPage.updateButton') || 'Mettre à jour'}
                </Button>
              </div>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Dialog de confirmation de suppression */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmer la suppression</DialogTitle>
            <DialogDescription>
              {t('dashboard.categoryTourPage.confirmDelete') || 'Êtes-vous sûr de vouloir supprimer cette catégorie ?'}
              <br />
              Cette action est irréversible.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>
              Annuler
            </Button>
            <Button variant="destructive" onClick={handleDeleteConfirm}>
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default CategoryTourDetails;
